import React from "react";
import autobind from "class-autobind";
import { FormGroup } from "../../form/group/index";
import { Input } from "../../form/input/index";
import { InputBox } from "../../form/input.box/index";
import { Button } from "../../button/index";
import { FaBars, FaChevronDown, FaChevronUp, FaMinusCircle } from "react-icons/fa";
import { Box, BoxSection } from "../../box/index";
import {Tooltip} from "../../tooltip/index";
import {arrayMove, SortableContainer, SortableElement, SortableHandle} from "react-sortable-hoc";
import {FormTreeSelect} from "../../form/tree/index";
import {RestaurantUtils} from "../../../../common/utils/restaurant/index";

type ItemType = RestaurantDishChoice;
type ListType = ItemType[];
type ToggleActive = (id: string) => void;
type HandleChange = (i: number, key: keyof ItemType, value: string | string[]) => void;
type HandleSetDefault = (i: number, quantity: 1 | 0) => void;
type HandleRemove = (i: number) => void;
type HandleSort = (data: { oldIndex: number; newIndex: number }) => void;

interface ComponentProps {
  r: RestaurantSchema;
  values: ListType;
  onChange: (values: ListType) => void;
}
interface ListProps {
  r: RestaurantSchema;
  items: ListType;
  change: HandleChange;
  remove: HandleRemove;
  toggleActive: ToggleActive;
  inActive: string[];
}
interface ItemProps {
  r: RestaurantSchema;
  item: ItemType;
  itemIndex: number;
  isLast: boolean;
  isActive: boolean;
  change: HandleChange;
  remove: HandleRemove;
  toggleActive: ToggleActive;
}

const DragHandle = SortableHandle(() => {
  return (
    <InputBox className="no-round width40 flex-center cursor-move nb-t">
      <p className="big font-bold">
        <FaBars/>
      </p>
    </InputBox>
  );
});
const SortableItem = SortableElement((props: ItemProps) => {
  const { item, itemIndex, change, remove, r, isLast, isActive, toggleActive } = props;
  return (
    <Box key={itemIndex} className={isLast ? "no-round" : "no-round nb-b"}>

      <div className="flex-line">

        <InputBox className="no-round width40 text-center nb-t nb-l cursor" onClick={() => toggleActive(item._id)}>
          <p className="big font-bold" style={{ lineHeight: "36px" }}>
            {isActive ? <FaChevronDown/> : <FaChevronUp/>}
          </p>
        </InputBox>

        <DragHandle/>

        <InputBox className="no-round width64 text-center nb-t">
          <p className="big font-bold" style={{ lineHeight: "36px" }}>{itemIndex + 1}</p>
        </InputBox>

        <div className="flex-grow" style={{ alignItems: "flex-end" }}>
          <FormGroup
            className="m-b-0"
            small_title={true}
            no_border={true}>
            <Input
              type="text"
              className="no-round nb-t "
              value={item.name}
              placeholder={"Name"}
              required={true}
              onChange={(e) => change(itemIndex, "name", e.target.value)}/>
          </FormGroup>
        </div>

        <Tooltip text="Delete" width={65} position="top" disable={itemIndex !== 0}>
          <Button type="button" className="no-round nb-t nb-r" paddinglr={5} color="white" onClick={() => remove(itemIndex)}>
            <FaMinusCircle/>
          </Button>
        </Tooltip>

      </div>

      {isActive && (
        <BoxSection paddingtb={15} paddinglr={10}>
          <FormTreeSelect
            id={`choices-${itemIndex}`}
            selected={item.dishes}
            nodes={RestaurantUtils.menu.getTreeFilter(r, (dish) => dish.type !== "combo")}
            onChange={(selectedValues) => change(itemIndex, "dishes", selectedValues)}
          />
        </BoxSection>
      )}

    </Box>
  );
});
const SortableItemContainer = SortableContainer((props: ListProps) => {
  return (
    <div>
      {props.items.map((item, i) => (
        <SortableItem
          key={`item-${i}`}
          index={i}
          r={props.r}
          item={item}
          itemIndex={i}
          isActive={props.inActive.indexOf(item._id) === -1}
          isLast={(props.items.length - 1) === i}
          change={props.change}
          remove={props.remove}
          toggleActive={props.toggleActive}
        />
      ))}
    </div>
  );
});

export class FieldDishChoices extends React.Component<ComponentProps, { inActive: string[] }> {

  constructor(props: ComponentProps) {
    super(props);
    autobind(this);
    this.state = {
      inActive: [],
    };
  }

  toggleActive: ToggleActive = (id: string) => {
    const inActive = [...this.state.inActive];
    const existing = inActive.indexOf(id);
    if (existing === -1)
      inActive.push(id);
    else
      inActive.splice(existing, 1);
    this.setState({ inActive });
  }
  remove: HandleRemove = (i) => {
    const values = ([ ...this.props.values ]);
    values.splice(i, 1);
    this.props.onChange(values);
  }
  change: HandleChange = (i, key, value) => {
    const values = [ ...this.props.values ];
    values[i][key] = value;
    this.props.onChange(values);
  }
  setDefault: HandleSetDefault = (i, quantity) => {
    this.props.onChange(this.props.values.map((item, index) => ({
      ...item,
      quantity: i === index ? quantity : 0,
    })));
  }
  onSortEnd: HandleSort = ({oldIndex, newIndex}) => {
    const { onChange, values } = this.props;
    onChange(arrayMove(values, oldIndex, newIndex));
  }

  render() {
    const { values, r } = this.props;
    return (
      <SortableItemContainer
        helperClass="zindex-15 bsl-2"
        pressDelay={50}
        useDragHandle={true}
        r={r}
        items={values}
        change={this.change}
        remove={this.remove}
        onSortEnd={this.onSortEnd}
        toggleActive={this.toggleActive}
        inActive={this.state.inActive}
      />
    );
  }
}
